import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { EXPERIENCE_TYPES, ExperienceType } from '../models';

@Component({
  selector: 'usb-experience-form',
  templateUrl: './experience-form.component.html',
  styleUrls: ['./experience-form.component.scss']
})
export class UsbExperienceFormComponent implements AfterViewInit {
  readonly experienceTypes = EXPERIENCE_TYPES;
  activeType: ExperienceType | null = null;
  activeOffsetX = 0;
  activeWidth = 0;
  showClearDialog = false;

  @Input() formGroup: FormGroup;
  @Input() selectedFile: any;
  @Input() selectedClient: any;
  @Input() availablePax: any[];
  @Input() loadingFiles: boolean;
  @Input() loadingPax: boolean;
  @Input() loadingCountries: boolean;
  @Input() showNewFileNameInput: boolean;
  @Input() showNewClientInput: boolean;
  @Input() existingFiles: any[];
  @Input() countriesData: any[];
  @Input() newFilePassenger: string;
  @Input() newFilePassengerError: boolean;
  @Input() newFilePhoneCode: string;
  @Input() newFilePhoneCodeError: boolean;
  @Input() newFilePhone: string;
  @Input() newFilePhoneError: boolean;
  @Input() newFileEmail: string;
  @Input() newFileEmailError: boolean;
  @Input() phoneCodeSelected: string;
  @Input() selectedFileId: string;
  @Input() selectedClientId: string;
  @Input() edit: boolean = false;
  @Input() requiredFieldMessage: (tab: string, field: string) => string;
  @Input() getFileLabel: (f: any) => string;
  @Input() getClientLabel: (c: any) => string;
  @Input() getClientsForSelectedFile: () => any[];
  @Input() isNuevoFileValid: () => boolean;
  @Input() blockInvalidKeys: (event: KeyboardEvent, type: string) => void;
  @Input() requestReady: boolean;

  @ViewChildren('pill') pillsRef: QueryList<ElementRef<HTMLButtonElement>>;

  @Output() fileChange = new EventEmitter<any>();
  @Output() clientChange = new EventEmitter<any>();
  @Output() paxChange = new EventEmitter<any>();
  @Output() requestTypeChange = new EventEmitter<any>();
  @Output() newFileToggle = new EventEmitter<void>();
  @Output() newClientToggle = new EventEmitter<void>();
  @Output() newFileSubmit = new EventEmitter<void>();
  @Output() newClientSubmit = new EventEmitter<any>();
  @Output() fileSearchInput = new EventEmitter<string>();
  @Output() clearSelectedFile = new EventEmitter<void>();
  @Output() passengerIdChange = new EventEmitter<any>();
  @Output() passengerFirstNameChange = new EventEmitter<string>();
  @Output() passengerLastNameChange = new EventEmitter<string>();
  @Output() newFilePassengerInput = new EventEmitter<string>();
  @Output() newFilePhoneCodeInput = new EventEmitter<string>();
  @Output() newFileCountrySelected = new EventEmitter<any>();
  @Output() newFilePhoneInput = new EventEmitter<string>();
  @Output() newFileEmailInput = new EventEmitter<string>();
  @Output() countrySelected = new EventEmitter<any>();
  @Output() phoneCodeInput = new EventEmitter<string>();
  @Output() clientFirstNameChange = new EventEmitter<string>();
  @Output() clientLastNameChange = new EventEmitter<string>();
  @Output() clientEmailChange = new EventEmitter<string>();
  @Output() clientPhoneChange = new EventEmitter<string>();
  @Output() submit = new EventEmitter<{
    file_id: string;
    client_id: string;
    passenger: any;
    requestType: 'quotation' | 'reservation' | 'on_hold';
    experienceType: string;
    destination: string;
    checkIn: string;
    checkOut: string;
    adults: number;
    childs: number;
    age_childs: string | null;
    detail: any;
    expandedNotes: string;
    comment_contact: string;
  }>();
  @Output() clear = new EventEmitter<void>();

  readonly ageOptions = Array.from({ length: 18 }, (_, i) => i);
  readonly experienceDestinationOptions = [
    'Buenos Aires, Argentina',
    'Madrid, España',
    'Miami, Estados Unidos',
    'Río de Janeiro, Brasil',
    'Roma, Italia'
  ];

  readonly modalityOptions = ['Regular', 'Privado'];
  readonly cancellationOptions = ['Cancelación gratuita', 'Reembolsable', 'No reembolsable', 'Indistinta'];
  readonly foodOptions = ['Con comida', 'Sin comida'];
  readonly halfFullOptions = ['Medio día', 'Full day'];
  readonly categoryOptions = ['Estándar', 'VIP'];

  constructor(private fb: FormBuilder) {}

  ngAfterViewInit(): void {
    this.measureActive();
    this.pillsRef.changes.subscribe(() => this.measureActive());
  }

  @HostListener('window:resize')
  onResize(): void {
    this.measureActive();
  }

  selectType(key: ExperienceType): void {
    this.formGroup.patchValue({ type: key, details: {} });
    this.activeType = key;
    this.measureActive();
  }

  private measureActive(): void {
    if (!this.activeType) {
      this.activeOffsetX = 0;
      this.activeWidth = 0;
      return;
    }
    const idx = this.experienceTypes.findIndex(t => t.key === this.activeType);
    const el = this.pillsRef?.toArray()[idx]?.nativeElement;
    if (el) {
      this.activeOffsetX = el.offsetLeft;
      this.activeWidth = el.offsetWidth;
    }
  }

  get childAgesArray(): FormArray {
    return this.formGroup.get('childAges') as FormArray;
  }

  get totalPassengers(): number {
    return (Number(this.formGroup.get('adults')?.value) || 0)
      + (Number(this.formGroup.get('children')?.value) || 0);
  }

  increment(field: 'adults' | 'children'): void {
    const current = Number(this.formGroup.get(field)?.value) || 0;
    this.formGroup.patchValue({ [field]: current + 1 });
    if (field === 'children') this.syncChildAges();
  }

  decrement(field: 'adults' | 'children'): void {
    const current = Number(this.formGroup.get(field)?.value) || 0;
    const next = Math.max(field === 'adults' ? 1 : 0, current - 1);
    this.formGroup.patchValue({ [field]: next });
    if (field === 'children') this.syncChildAges();
  }

  private syncChildAges(): void {
    const target = Number(this.formGroup.get('children')?.value) || 0;
    while (this.childAgesArray.length < target) {
      this.childAgesArray.push(this.fb.control(''));
    }
    while (this.childAgesArray.length > target) {
      this.childAgesArray.removeAt(this.childAgesArray.length - 1);
    }
  }

  onChildAgeChange(index: number, age: string): void {
    this.childAgesArray.at(index).setValue(age);
  }

  getDetail(): any {
    return this.formGroup.get('details')?.value || {};
  }

  patchDetail(key: string, value: any): void {
    const current = this.formGroup.get('details')?.value || {};
    this.formGroup.get('details')?.patchValue({ ...current, [key]: value });
  }

  setDetail(key: 'modality' | 'cancellation' | 'food' | 'halfFull' | 'category', value: string): void {
    const current = this.formGroup.get('details')?.value || {};
    this.formGroup.get('details')?.patchValue({ ...current, [key]: [value] });
  }

  toggleExpanded(): void {
    this.formGroup.patchValue({ expanded: !this.formGroup.get('expanded')?.value });
  }

  confirmClear(): void { this.showClearDialog = true; }
  cancelClear(): void { this.showClearDialog = false; }

  confirmClearAction(): void {
    this.formGroup.reset({
      destination: '', checkIn: '', checkOut: '',
      adults: 2, children: 0, childAges: [],
      budget: '', nationality: '',
      type: 'city', details: {},
      cancellation: [], expanded: false,
      searchNotes: '', comments: '',
      requestType: 'quotation',
      file_id: '', fileName: '',
      client: { client_id: '', first_name: '', last_name: '', email: '', phone_code: '', phone: '' },
      passenger: { id: '', first_name: '', last_name: '' }
    });
    this.activeType = 'city';
    this.measureActive();
    this.showClearDialog = false;
    this.clear.emit();
  }

  onSubmit(): void {
    if (this.formGroup.invalid || !this.requestReady) return;
    this.submit.emit(this.formGroup.value);
  }

  openDatePicker(field: 'checkIn' | 'checkOut'): void {
  }
}
